'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface TocItem {
  id: string;
  label: string;
}

interface TableOfContentsProps {
  items: TocItem[];
}

export default function TableOfContents({ items }: TableOfContentsProps) {
  const [collapsed, setCollapsed] = useState(false);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <nav
      aria-label="Table of contents"
      className="glass-card rounded-2xl border border-border/40 overflow-hidden"
    >
      {/* Header */}
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left"
        onClick={() => setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
      >
        <span className="font-display font-bold text-sm text-foreground tracking-wide uppercase flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-accent" />
          In This Article
        </span>
        <span className="text-muted-foreground lg:hidden">
          {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </span>
      </button>

      {/* Items */}
      <div className={`${collapsed ? 'hidden lg:block' : 'block'} border-t border-border/30`}>
        <ol className="py-3 px-3 space-y-0.5">
          {items.map((item, i) => (
            <li key={item.id}>
              <button
                onClick={() => scrollTo(item.id)}
                className="w-full text-left flex items-start gap-3 px-3 py-2 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-primary/8 transition-all duration-150 group"
              >
                <span className="font-mono-code text-xs text-muted-foreground/50 mt-0.5 w-4 flex-shrink-0 group-hover:text-accent transition-colors">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="leading-snug">{item.label}</span>
              </button>
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}
